import moment from "moment";
import { useState } from "react";
import { Row } from "../../components/list/NestedList";
import { IProcessedScan } from "../../models/stat";
import HBScanStatService from "../../services/stat/HBScanStatService";
import DateUtils from "../../utils/dateUtils";
import FloorService from "../../services/floor/FloorService";
import IFloor from "../../models/floor";
import { theme } from "../../styles/themes/mainTheme";
import ticketService from "../../services/ticket/TicketService";
import { ITicketDetail } from "../../models/ticket/ticketDetail";
import { STRING_SPLIT_CHAR } from "../../utils/stringUtils";
import IEmployee from "../../models/employee";
import DeepSet from "../../utils/deepSet";
import buildingService from "../../services/building/BuildingService";
import IAdminPageBuildingDetail from "../../models/adminPage/adminPageBuildingDetail";

const getTicketRequest = (startRequest?: string, endRequest?: string, startImageKeys?: string, endImageKeys?: string) => {
    let request = startRequest || "";
    let imageKeys = startImageKeys || "";

    if (startRequest && endRequest) request += "\n\n";
    if (startImageKeys && endImageKeys) imageKeys += STRING_SPLIT_CHAR;

    request += endRequest || "";
    imageKeys += endImageKeys || "";

    if (startRequest === endRequest) request = startRequest || "";
    if (startImageKeys === endImageKeys) imageKeys = startImageKeys || "";

    if (!request && !imageKeys) return [];
    return [request, imageKeys];
}

const getInOutLocalScans = (
    currentInOutScan: IProcessedScan,
    sortedInOutScans: IProcessedScan[],
    localScans: IProcessedScan[]
) => {
    const nextScan = sortedInOutScans
        .filter(
            (scan) =>
                scan.start.userId === currentInOutScan.start.userId &&
                moment(scan.start.date).isAfter(currentInOutScan.start.date)
        )
        .pop();

    let nextDate = nextScan && moment(nextScan.start.date);

    const scanDate = moment(currentInOutScan.start.date);
    const scanEndDate = moment(currentInOutScan.start.date).add(
        currentInOutScan.minutes,
        "minute"
    );

    let inOutLocalScans = localScans.filter((localScan) =>
        moment(localScan.start.date).isBetween(
            scanDate,
            currentInOutScan.minutes < 1 && nextScan ? nextDate : scanEndDate,
            undefined,
            "[]"
        )
    );

    if (!nextDate && currentInOutScan.minutes < 1) {
        inOutLocalScans = localScans.filter((localScan) =>
            moment(localScan.start.date).isSameOrAfter(scanDate)
        );
    }

    return { inOutLocalScans, scanDate, scanEndDate };
};

const groupScansByDate = (
    userGroup: Row,
    scanDate: moment.Moment,
    scanMinutes: number
) => {
    let dateGroup = userGroup.internalNodes?.find((group) =>
        scanDate.isSame(group.items[0], "day")
    );

    if (!dateGroup) {
        dateGroup = {
            items: [scanDate.toDate(), 0],
            internalNodes: [],
        };
        userGroup.internalNodes?.push(dateGroup);
    }
    (dateGroup.items[1] as number) += scanMinutes / 60;

    return dateGroup;
};

const groupScansByInOut = (
    dateGroup: Row,
    scanDate: moment.Moment,
    scanEndDate: moment.Moment,
    scanMinutes: number,
    isAutoGenerated: boolean
) => {
    const scanInOutHours = DateUtils.formatInOutHours(
        scanDate.toDate(),
        scanMinutes < 1 ? "- - -" : scanEndDate.toDate()
    );

    let inOutGroup = dateGroup.internalNodes?.find(
        (group) => group.items[0] === scanInOutHours
    );

    if (!inOutGroup) {
        inOutGroup = {
            items: [scanInOutHours, 0],
            leafNodes: [],
            color:
                scanMinutes < 1 || isAutoGenerated
                    ? theme.palette.error.main
                    : theme.palette.text.primary,
        };
        dateGroup.internalNodes?.push(inOutGroup);
    }

    (inOutGroup.items[1] as number) += scanMinutes / 60;

    return inOutGroup;
};

const groupScans = (
    scans: IProcessedScan[],
    localScans: IProcessedScan[],
    floors: IFloor[],
    detailedTickets: ITicketDetail[],
    buildings: IAdminPageBuildingDetail[]
) => {
    const groupedScans = scans.reduce((acc, scan) => {
        const userLocalScans = localScans.filter(
            (localScan) => localScan.start.userId === scan.start.userId
        );
        const { inOutLocalScans, scanDate, scanEndDate } = getInOutLocalScans(
            scan,
            scans,
            userLocalScans
        );

        if (scan.minutes < 1 && inOutLocalScans.length < 1) return acc;

        if (!acc.has(scan.start.userId)) {
            acc.set(scan.start.userId, {
                items: [scan.start.username, 0],
                internalNodes: [],
            });
        }
        const userGroup = acc.get(scan.start.userId);
        if (!userGroup) return acc;
        (userGroup.items[1] as number) += scan.minutes / 60;

        const dateGroup = groupScansByDate(userGroup, scanDate, scan.minutes);

        const inOutGroup = groupScansByInOut(
            dateGroup,
            scanDate,
            scanEndDate,
            scan.minutes,
            scan.isAutoGenerated
        );
        inOutLocalScans.forEach((localScan) => {
            const floorName = floors.find((floor) => floor.id === localScan.start.floorId)?.name ?? "";
            const startDetailedTicket = detailedTickets.find((detailedTicket) => detailedTicket.ticket.scanId === `${localScan.start.userId}${STRING_SPLIT_CHAR}${localScan.start.localCode}${STRING_SPLIT_CHAR}${localScan.start.date}`);
            const endDetailedTicket = localScan.start.localCode === localScan.end.localCode ?
                detailedTickets.find((detailedTicket) => detailedTicket.ticket.scanId === `${localScan.end.userId}${STRING_SPLIT_CHAR}${localScan.end.localCode}${STRING_SPLIT_CHAR}${localScan.end.date}`) : undefined;

            inOutGroup.leafNodes?.push({
                items: [
                    DateUtils.formatTime(localScan.start.date),
                    buildings.find(b => b.id == floors.find((floor) => floor.id === localScan.start.floorId)?.buildingId || 0)?.name || "",
                    floorName,
                    localScan.start.localName,
                    `${Math.round(localScan.minutes)} min`,
                ],
                modalData: getTicketRequest(
                    startDetailedTicket?.ticketHeader.request,
                    endDetailedTicket?.ticketHeader.request,
                    startDetailedTicket?.ticketHeader.imageKeys,
                    endDetailedTicket?.ticketHeader.imageKeys
                )
            });
        });



        return acc;
    }, new Map<number, Row>());

    return groupedScans;
};

const useInOutRoutes = () => {
    const [employees, setEmployees] = useState<IEmployee[]>([]);
    const [selectedEmployees, setSelectedEmployees] = useState<number[]>([]);
    const [inOutRouteRows, setInOutRoutesRows] = useState<Row[]>([]);
    const setInOutRoutes = (propertyGroupId: number, start: Date, end: Date | undefined, selectedEmployees: number[]) => {

        if (!end || propertyGroupId <= 0) return;
        buildingService.GetBuildingsDetails().then((buildings) => {
            HBScanStatService.getPropertyGroupScans(propertyGroupId, start, end).then(
                (localScans) => {
                    HBScanStatService.getInOutScansByPropertyId(propertyGroupId, start, end).then(
                        async (scans) => {
                            setEmployees(new DeepSet(
                                scans.map((s) => ({ id: s.start.userId, name: s.start.username }))
                            ).values())

                            if (selectedEmployees.length)
                                scans = scans.filter((scan) =>
                                    selectedEmployees.includes(scan.start.userId)
                                );
                            const floors = await FloorService.GetFloors(propertyGroupId);
                            const detailedTickets = await ticketService.GetTicketsByPropertyGroup(propertyGroupId);
                            scans = scans.sort(
                                (a, b) =>
                                    moment(b.start.date).unix() - moment(a.start.date).unix()
                            );
                            localScans = localScans.sort(
                                (a, b) =>
                                    moment(a.start.date).unix() - moment(b.start.date).unix()
                            );

                            const groupedScans = groupScans(
                                scans,
                                localScans,
                                floors,
                                detailedTickets,
                                buildings
                            );
                            const result: Row[] = Array.from(groupedScans.values());

                            setInOutRoutesRows(result);
                        }
                    );
                }
            )
        })

    };

    return {
        inOutRouteRows,
        setInOutRoutes,
        employees,
        setSelectedEmployees,
        selectedEmployees,
    };
};

export default useInOutRoutes;
